import { useEffect, useState } from 'react'
import { getCssVariableValue } from '../helpers/getCssVariableValue'

/**
 * Custom hook to detect when CSS stylesheets are fully loaded and CSS variables are available.
 * This is particularly important for Stripe elements which need styles at initialization time.
 *
 * @returns boolean indicating whether styles are ready for use
 */
export const useStylesReady = (): boolean => {
  const [stylesReady, setStylesReady] = useState<boolean>(false)

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null
    let isMounted = true

    const checkStylesReady = () => {
      // Early return if component has unmounted
      if (!isMounted) return

      const appCssLink = document.querySelector(
        'link[href*="app.css"]'
      ) as HTMLLinkElement
      const nonCriticalCssLink = document.querySelector(
        'link[href*="non-critical.css"]'
      ) as HTMLLinkElement

      const isAppCssReady = appCssLink?.sheet !== null
      const isNonCriticalReady =
        nonCriticalCssLink?.sheet !== null ||
        nonCriticalCssLink?.media === 'all'
      const isDocumentReady = document.readyState === 'complete'

      // In test environment, assume styles are ready if no CSS links exist
      const isTestEnvironment = process.env.NODE_ENV === 'test'

      // If we're in test environment and no CSS links exist, consider styles ready
      if (isTestEnvironment && !appCssLink && !nonCriticalCssLink) {
        setStylesReady(true)
        return
      }

      // Also check if CSS variables are actually available (skip in test environment)
      const testCssVar = isTestEnvironment
        ? 'test-value'
        : getCssVariableValue('--ls-color-bg-neutral-top')
      const hasCssVariables = testCssVar !== null

      if (
        isAppCssReady &&
        (isNonCriticalReady || isDocumentReady) &&
        hasCssVariables
      ) {
        setStylesReady(true)
        return
      }

      // If styles aren't ready, wait a bit and check again (with cleanup)
      timeoutId = setTimeout(checkStylesReady, 50)
    }

    checkStylesReady()

    // Cleanup function to prevent memory leaks
    return () => {
      isMounted = false
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [])

  return stylesReady
}
